/**
 * Glossary — alphabetical Bodenblick glossary (public, no auth).
 *
 * Route: /glossar
 *
 * Design: editorial magazine-style. Sticky alphabet sidebar on desktop,
 * search at top, anchor-targeted term blocks. Each entry: short answer
 * + 1-3 sentence "how Bodenblick uses it" follow-up. Linkable via
 * `/glossar#ndvi`. Every ExplainPanel/StoryCard across the site can
 * point here for "→ mehr im Glossar".
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowLeft, Leaf, BookOpen, MagnifyingGlass, Sparkle, ArrowUp,
} from "@phosphor-icons/react";
import { TERMS, CATEGORY_COLOR } from "@/lib/glossary";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Glossary() {
  const [query, setQuery] = useState("");
  const sorted = useMemo(
    () => [...TERMS].sort((a, b) => a.term.localeCompare(b.term, "de")),
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((t) => {
      const hay = [
        t.term, t.answer, t.use || "", t.category,
        ...(t.aliases || []),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [sorted, query]);

  const groups = useMemo(() => {
    const out = {};
    for (const t of filtered) {
      const letter = (t.term[0] || "?").toUpperCase();
      (out[letter] ||= []).push(t);
    }
    return out;
  }, [filtered]);

  const availableLetters = useMemo(
    () => new Set(sorted.map((t) => (t.term[0] || "").toUpperCase())),
    [sorted]
  );

  return (
    <>
      <Helmet>
        <title>Bodenblick-Glossar · Alle Begriffe einfach erklärt</title>
        <meta
          name="description"
          content="Was bedeutet NDVI? Was ist Fruktan? Wozu Sentinel-2? Das Bodenblick-Glossar erklärt alle Agrar-, Satelliten- und Wetter-Begriffe in einem Satz."
        />
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A]" data-testid="glossary-page">
        <header className="border-b border-white/15 bg-[#121212]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#00FA9A] hover:underline" data-testid="glossary-back">
              <ArrowLeft size={14} /> <Leaf size={14} weight="fill" /> Bodenblick
            </Link>
            <Link to="/feldfruechte" className="text-xs text-white/60 hover:text-[#00FA9A]">→ Feldfrüchte-Erklärer</Link>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <section className="max-w-3xl mb-8 relative">
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden>
              <div className="absolute -top-10 -left-12 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(circle, #00FA9A 0%, transparent 65%)" }} />
              <div className="absolute top-24 right-0 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: "radial-gradient(circle, #FFC24B 0%, transparent 65%)" }} />
              <svg className="absolute top-0 right-0 w-40 h-40 opacity-[0.07]" viewBox="0 0 100 100">
                <defs>
                  <pattern id="dotgrid" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="1" fill="#FFFFFF" />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#dotgrid)" />
              </svg>
            </div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#00FA9A] font-mono mb-3 inline-flex items-center gap-2">
              <BookOpen size={12} weight="fill" /> Lexikon · {sorted.length} Begriffe
            </p>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-[5rem] font-bold tracking-tighter leading-[0.9] bb-accent-stripe">
              Glossar.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
              NDVI? Fruktan? FIRMS? Hier ein Satz pro Begriff — plus, wie Bodenblick
              ihn nutzt. Such-feld unten findet alles in Echtzeit.
            </p>

            <div className="mt-6 relative">
              <MagnifyingGlass
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suche … z. B. NDVI, Fruktan, Bienenwetter"
                className="w-full pl-10 pr-3 py-3 bg-[#121212] border border-white/15 rounded-sm text-base focus:outline-none focus:border-[#00FA9A]/40"
                data-testid="glossary-search"
                autoComplete="off"
              />
            </div>
          </section>

          <div className="grid lg:grid-cols-[1fr_auto] gap-8">
            <div className="space-y-10" data-testid="glossary-list">
              {Object.keys(groups).length === 0 ? (
                <div className="border border-dashed border-white/15 p-8 text-center text-white/60 text-sm">
                  Kein Treffer. Probiere einen anderen Suchbegriff —
                  oder schau einfach das ganze Lexikon ohne Filter durch.
                </div>
              ) : (
                Object.keys(groups).sort().map((letter) => (
                  <section
                    key={letter}
                    id={`letter-${letter}`}
                    className="scroll-mt-20"
                    data-testid={`glossary-section-${letter.toLowerCase()}`}
                  >
                    <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#00FA9A] tracking-tighter leading-none mb-4">
                      {letter}
                    </h2>
                    <div className="space-y-4">
                      {groups[letter].map((t, i) => (
                        <motion.article
                          key={t.id}
                          id={t.id}
                          initial={{ opacity: 0, y: 12 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.2 }}
                          transition={{ duration: 0.4, delay: i * 0.04 }}
                          className="border border-white/15 bg-[#121212] p-4 sm:p-5 scroll-mt-20"
                          style={{
                            borderLeftWidth: 4,
                            borderLeftColor: CATEGORY_COLOR[t.category] || "#A1A1AA",
                          }}
                          data-testid={`glossary-term-${t.id}`}
                        >
                          <div className="flex items-baseline justify-between gap-3 flex-wrap">
                            <h3 className="font-display text-xl font-bold tracking-tight text-white">
                              {t.term}
                            </h3>
                            <span
                              className="text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-sm"
                              style={{
                                background: (CATEGORY_COLOR[t.category] || "#A1A1AA") + "18",
                                color: CATEGORY_COLOR[t.category] || "#A1A1AA",
                              }}
                            >
                              {t.category}
                            </span>
                          </div>
                          <p className="mt-2 text-[15px] leading-relaxed text-white/85">
                            {t.answer}
                          </p>
                          {t.use && (
                            <p className="mt-2 text-[13px] leading-relaxed text-white/60 flex gap-2">
                              <Sparkle
                                size={12}
                                weight="fill"
                                className="mt-1 shrink-0"
                                style={{ color: CATEGORY_COLOR[t.category] || "#A1A1AA" }}
                              />
                              <span><strong className="text-white/80">In Bodenblick:</strong> {t.use}</span>
                            </p>
                          )}
                        </motion.article>
                      ))}
                    </div>
                  </section>
                ))
              )}
            </div>

            {/* Alphabet rail — desktop only */}
            <aside
              className="hidden lg:block sticky top-20 self-start"
              data-testid="glossary-alphabet"
            >
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mb-2">Springe zu</div>
              <ul className="space-y-0.5">
                {ALPHABET.map((l) => {
                  const has = availableLetters.has(l) && groups[l];
                  return (
                    <li key={l}>
                      <a
                        href={has ? `#letter-${l}` : undefined}
                        aria-disabled={!has}
                        className={`block w-8 h-7 leading-7 text-center text-sm font-mono rounded-sm transition-colors ${
                          has
                            ? "text-[#00FA9A] hover:bg-[#00FA9A]/20 hover:text-white cursor-pointer"
                            : "text-white/20 cursor-default"
                        }`}
                      >
                        {l}
                      </a>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="mt-4 w-8 h-8 rounded-sm bg-[#F5F5F5] text-[#0A0A0A] inline-flex items-center justify-center hover:bg-[#00FA9A]/20"
                title="Nach oben"
                data-testid="glossary-scroll-top"
              >
                <ArrowUp size={12} />
              </button>
            </aside>
          </div>

          <p className="text-[11px] text-zinc-400 font-mono text-center mt-16">
            Fehlt ein Begriff? <a href="mailto:vision@bodenblick.com" className="underline">vision@bodenblick.com</a>
          </p>
        </main>
      </div>
    </>
  );
}
